import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

// Enhanced Custom Dropdown Component with Animations
const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  icon: Icon,
  renderOption = null,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none cursor-pointer hover:shadow-md ${
          isDarkMode 
            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-650 focus:bg-gray-650' 
            : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:bg-gray-50'
        }`}
      >
        <div className="flex items-center">
          {Icon && <Icon className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />}
          <span className="text-sm">{selectedOption?.label || placeholder}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Animated Dropdown Menu */}
      <div className={`absolute z-50 w-full mt-1 border rounded-xl shadow-lg max-h-60 overflow-y-auto transition-all duration-200 transform origin-top ${
        isOpen 
          ? 'opacity-100 scale-y-100' 
          : 'opacity-0 scale-y-95 pointer-events-none'
      } ${
        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
      }`}>
        {options.map((option, index) => (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={`w-full flex items-center px-4 py-3 text-left text-sm transition-all duration-150 ${
              isDarkMode 
                ? 'text-gray-200 hover:bg-gray-600 hover:text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            } ${option.value === value ? (isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100') : ''} ${
              isOpen ? 'animate-in slide-in-from-top-2 duration-200' : ''
            }`}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {renderOption ? renderOption(option) : (
              <>
                {option.icon && <option.icon className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />}
                <span>{option.label}</span>
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CustomDropdown; 